import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import {
  ArrowLeft,
  FileText,
  AlertTriangle,
  Shield,
  ChevronDown,
  ChevronUp,
  Scale,
  Eye,
  Swords,
  MessageSquare,
  HelpCircle,
  Download,
  Copy,
  Loader2,
  CheckCircle2,
  XCircle,
  Info,
} from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/* ─── Scorecard gauge ─── */
const ScoreGauge = ({ label, score, maxScore = 10 }) => {
  const pct = (score / maxScore) * 100;
  const color = pct >= 70 ? 'bg-emerald-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500';
  const textColor = pct >= 70 ? 'text-emerald-700' : pct >= 40 ? 'text-amber-700' : 'text-red-700';
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-baseline">
        <span className="text-xs font-medium text-gray-600">{label}</span>
        <span className={`text-sm font-bold ${textColor}`}>{score}/10</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

/* ─── Risk badge ─── */
const RiskBadge = ({ severity }) => {
  const styles = {
    low: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    moderate: 'bg-amber-100 text-amber-700 border-amber-200',
    high: 'bg-orange-100 text-orange-700 border-orange-200',
    critical: 'bg-red-100 text-red-700 border-red-200',
  };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${styles[severity] || styles.moderate}`}>
      {severity?.toUpperCase()}
    </span>
  );
};

const ContractAnalysis = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);

  // Interactive states
  const [expandedClauses, setExpandedClauses] = useState({});
  const [equityViewOn, setEquityViewOn] = useState(false);
  const [traps, setTraps] = useState(null);
  const [trapsLoading, setTrapsLoading] = useState(false);
  const [activeResponseType, setActiveResponseType] = useState(null);
  const [generatedDoc, setGeneratedDoc] = useState(null);
  const [generatingDoc, setGeneratingDoc] = useState(false);
  const [explainLoading, setExplainLoading] = useState({});
  const [explanations, setExplanations] = useState({});

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchContractDetail();
  }, [contractId]);

  const fetchContractDetail = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/contract/detail/${contractId}`, { headers });
      setContract(res.data);
      setAnalysis(res.data.analysis);

      // If uploaded but not analyzed, trigger analysis
      if (res.data.status === 'uploaded' && !res.data.analysis) {
        toast({ title: 'Analyzing...', description: 'Running AI analysis on your contract.' });
        const analyzeRes = await axios.post(
          `${API_BASE}/api/contract/analyze`,
          { contract_id: contractId },
          { headers }
        );
        setAnalysis(analyzeRes.data.analysis);
        setContract(prev => ({ ...prev, analysis: analyzeRes.data.analysis, status: 'analyzed' }));
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load contract.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const toggleClause = (idx) => {
    setExpandedClauses(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const expandAllClauses = () => {
    if (!analysis?.clauses) return;
    const allExpanded = analysis.clauses.every((_, i) => expandedClauses[i]);
    if (allExpanded) {
      setExpandedClauses({});
    } else {
      const all = {};
      analysis.clauses.forEach((_, i) => { all[i] = true; });
      setExpandedClauses(all);
    }
  };

  /* ─── Explain clause ─── */
  const handleExplainClause = async (idx, clauseText) => {
    if (explanations[idx]) return; // Already explained
    setExplainLoading(prev => ({ ...prev, [idx]: true }));
    try {
      const res = await axios.post(
        `${API_BASE}/api/contract/explain-clause`,
        { clause_text: clauseText },
        { headers }
      );
      setExplanations(prev => ({ ...prev, [idx]: res.data.explanation }));
    } catch {
      toast({ title: 'Failed to explain clause', variant: 'destructive' });
    } finally {
      setExplainLoading(prev => ({ ...prev, [idx]: false }));
    }
  };

  /* ─── Show traps ─── */
  const handleShowTraps = async () => {
    if (traps) { setTraps(null); return; }
    setTrapsLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/contract/show-traps`,
        { contract_id: contractId },
        { headers }
      );
      setTraps(res.data.traps);
    } catch {
      toast({ title: 'Failed to detect traps', variant: 'destructive' });
    } finally {
      setTrapsLoading(false);
    }
  };

  /* ─── Generate response document ─── */
  const handleGenerateResponse = async (type) => {
    setActiveResponseType(type);
    setGeneratedDoc(null);
    setGeneratingDoc(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/contract/generate-response`,
        { contract_id: contractId, response_type: type },
        { headers }
      );
      setGeneratedDoc(res.data.document);
    } catch {
      toast({ title: 'Failed to generate document', variant: 'destructive' });
    } finally {
      setGeneratingDoc(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast({ title: 'Copied to clipboard' });
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <p className="text-gray-500">Contract not found.</p>
        <Button variant="outline" onClick={() => navigate('/dashboard/contract-portal')}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Portal
        </Button>
      </div>
    );
  }

  const hasAnalysis = analysis && !analysis.error;

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6" data-testid="contract-analysis-page">
      {/* Nav */}
      <button
        data-testid="back-to-portal-btn"
        onClick={() => navigate('/dashboard/contract-portal')}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Contract Portal
      </button>

      {/* Title Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            {contract.metadata?.title || contract.original_filename}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {contract.metadata?.contract_type && (
              <span className="capitalize">{contract.metadata.contract_type.replace(/_/g, ' ')}</span>
            )}
            {contract.metadata?.industry && ` · ${contract.metadata.industry.replace(/_/g, ' ')}`}
            {contract.created_at && ` · ${new Date(contract.created_at).toLocaleDateString()}`}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            data-testid="show-traps-btn"
            onClick={handleShowTraps}
            disabled={trapsLoading}
            className="text-red-600 border-red-200 hover:bg-red-50"
          >
            {trapsLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Swords className="w-4 h-4 mr-1" />}
            {traps ? 'Hide Traps' : 'Show Me the Traps'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="equity-view-toggle"
            onClick={() => setEquityViewOn(!equityViewOn)}
            className={equityViewOn ? 'bg-purple-100 border-purple-300 text-purple-700' : ''}
          >
            <Scale className="w-4 h-4 mr-1" />
            Equity View {equityViewOn ? 'ON' : 'OFF'}
          </Button>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
        <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>
          AI-powered analysis for educational purposes only. This does not constitute legal advice and does not create an attorney-client relationship. 
          Consult a qualified professional for high-stakes matters.
        </span>
      </div>

      {!hasAnalysis ? (
        <div className="bg-white rounded-xl border p-8 text-center">
          <Loader2 className="w-10 h-10 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Analysis in progress or unavailable. Please wait...</p>
        </div>
      ) : (
        <>
          {/* ─── Executive Summary ─── */}
          <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-testid="executive-summary">
            <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" /> Executive Summary
            </h2>
            <p className="text-gray-700 leading-relaxed">{analysis.executive_summary}</p>
            {analysis.contract_type && (
              <p className="mt-3 text-sm text-gray-500">
                <strong>Identified Type:</strong> <span className="capitalize">{analysis.contract_type}</span>
              </p>
            )}
            {analysis.parties?.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {analysis.parties.map((p, i) => (
                  <span key={i} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                    {p.name} — <em>{p.role}</em>
                  </span>
                ))}
              </div>
            )}
          </section>

          {/* ─── Contract Scorecard ─── */}
          {analysis.scorecard && (
            <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-testid="contract-scorecard">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" /> Contract Scorecard
              </h2>
              <p className="text-xs text-gray-500 mb-4">Based on internal heuristics — not legal guarantees.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {Object.entries(analysis.scorecard).map(([key, val]) => (
                  <ScoreGauge key={key} label={key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} score={val?.score || 0} />
                ))}
              </div>
            </section>
          )}

          {/* ─── Key Facts ─── */}
          {analysis.key_facts && (
            <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-testid="key-facts">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-600" /> Key Contract Facts
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
                {Object.entries(analysis.key_facts).map(([key, val]) => (
                  <div key={key} className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="text-sm text-gray-500 capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="text-sm font-medium text-gray-900 text-right max-w-[60%]">{val || 'Not specified'}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ─── Traps Section (shown when toggled) ─── */}
          {traps && (
            <section className="bg-red-50 rounded-xl border border-red-200 shadow-sm p-6" data-testid="traps-section">
              <h2 className="text-lg font-bold text-red-800 mb-3 flex items-center gap-2">
                <Swords className="w-5 h-5 text-red-600" /> Contract Traps Detected
              </h2>
              <div className="prose prose-sm max-w-none text-red-900 whitespace-pre-wrap">
                {traps}
              </div>
            </section>
          )}

          {/* ─── Equity View (shown when toggled) ─── */}
          {equityViewOn && analysis.equity_review && (
            <section className="bg-purple-50 rounded-xl border border-purple-200 shadow-sm p-6" data-testid="equity-review">
              <h2 className="text-lg font-bold text-purple-800 mb-4 flex items-center gap-2">
                <Scale className="w-5 h-5 text-purple-600" /> Equity & Fairness Review
              </h2>
              <div className="space-y-4">
                {Object.entries(analysis.equity_review).map(([key, val]) => (
                  <div key={key}>
                    <h3 className="text-sm font-semibold text-purple-700 capitalize mb-1">
                      {key.replace(/_/g, ' ')}
                    </h3>
                    <p className="text-sm text-gray-700">{val}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ─── Risk Alerts ─── */}
          {analysis.risk_flags?.length > 0 && (
            <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-testid="risk-alerts">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" /> Risk Alerts
              </h2>
              <div className="space-y-3">
                {analysis.risk_flags.map((flag, i) => (
                  <div
                    key={i}
                    className={`rounded-lg border p-4 ${
                      flag.severity === 'critical' ? 'border-red-300 bg-red-50' :
                      flag.severity === 'high' ? 'border-orange-300 bg-orange-50' :
                      flag.severity === 'moderate' ? 'border-amber-200 bg-amber-50' :
                      'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <RiskBadge severity={flag.severity} />
                      <span className="font-semibold text-gray-900 text-sm">{flag.title}</span>
                    </div>
                    <p className="text-sm text-gray-700">{flag.description}</p>
                    {flag.clause_reference && (
                      <p className="text-xs text-gray-500 mt-1">Clause: {flag.clause_reference}</p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ─── Clause Explorer ─── */}
          {analysis.clauses?.length > 0 && (
            <section className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="clause-explorer">
              <div className="p-6 pb-3 flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-blue-600" /> Clause Explorer
                </h2>
                <button onClick={expandAllClauses} className="text-xs text-blue-600 hover:underline">
                  {analysis.clauses.every((_, i) => expandedClauses[i]) ? 'Collapse All' : 'Expand All'}
                </button>
              </div>
              <div className="divide-y divide-gray-100">
                {analysis.clauses.map((clause, idx) => (
                  <div key={idx} className="px-6 py-4">
                    <button
                      data-testid={`clause-toggle-${idx}`}
                      onClick={() => toggleClause(idx)}
                      className="w-full flex items-center justify-between text-left"
                    >
                      <div className="flex items-center gap-3">
                        <RiskBadge severity={clause.risk_level} />
                        <span className="font-medium text-gray-900 text-sm">{clause.name}</span>
                      </div>
                      {expandedClauses[idx] ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </button>

                    {expandedClauses[idx] && (
                      <div className="mt-3 pl-4 border-l-2 border-blue-200 space-y-3 text-sm">
                        {clause.original_text_snippet && (
                          <div>
                            <span className="text-xs font-semibold text-gray-500 uppercase">Original Text</span>
                            <p className="text-gray-600 italic mt-1">"{clause.original_text_snippet}"</p>
                          </div>
                        )}
                        <div>
                          <span className="text-xs font-semibold text-gray-500 uppercase">Plain English</span>
                          <p className="text-gray-700 mt-1">{clause.plain_english}</p>
                        </div>
                        <div>
                          <span className="text-xs font-semibold text-gray-500 uppercase">Why It Matters</span>
                          <p className="text-gray-700 mt-1">{clause.why_it_matters}</p>
                        </div>
                        <div>
                          <span className="text-xs font-semibold text-gray-500 uppercase">Watch Out For</span>
                          <p className="text-gray-700 mt-1">{clause.watch_out_for}</p>
                        </div>

                        {/* Explain Like I'm Not a Lawyer */}
                        <div className="pt-2">
                          <button
                            data-testid={`explain-clause-${idx}`}
                            onClick={() => handleExplainClause(idx, clause.original_text_snippet || clause.plain_english)}
                            disabled={explainLoading[idx]}
                            className="text-xs flex items-center gap-1.5 text-blue-600 hover:text-blue-800 font-medium"
                          >
                            {explainLoading[idx] ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <HelpCircle className="w-3.5 h-3.5" />
                            )}
                            Explain Like I'm Not a Lawyer
                          </button>
                          {explanations[idx] && (
                            <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900">
                              {explanations[idx]}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ─── Negotiation Points ─── */}
          {analysis.negotiation_points?.length > 0 && (
            <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6" data-testid="negotiation-points">
              <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-blue-600" /> Negotiation Points
              </h2>
              <div className="space-y-4">
                {analysis.negotiation_points.map((pt, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-4">
                    <p className="font-medium text-gray-900 text-sm">{pt.point}</p>
                    <p className="text-sm text-gray-600 mt-1">{pt.reason}</p>
                    {pt.suggested_language && (
                      <p className="text-xs text-blue-700 bg-blue-50 p-2 rounded mt-2 border border-blue-100">
                        <strong>Suggested:</strong> {pt.suggested_language}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ─── Action Buttons: Response Generator & Negotiation Mode ─── */}
          <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4" data-testid="response-generator">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <Swords className="w-5 h-5 text-blue-600" /> Generate Response Documents
            </h2>
            <p className="text-sm text-gray-500">Select a document type to generate. The AI will draft it based on your contract analysis.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { key: 'negotiation_questions', label: 'Negotiation Questions', desc: 'Smart questions to ask' },
                { key: 'clarification_request', label: 'Clarification Request', desc: 'Letter requesting clarity' },
                { key: 'conditional_acceptance', label: 'Conditional Acceptance', desc: 'Accept with conditions' },
                { key: 'rebuttal_notice', label: 'Rebuttal Notice', desc: 'Challenge unfair terms' },
                { key: 'redline_summary', label: 'Redline Summary', desc: 'Proposed clause changes' },
                { key: 'attorney_questions', label: 'Attorney Review Questions', desc: 'Questions for your lawyer' },
                { key: 'proposed_revisions', label: 'Proposed Revisions', desc: 'Suggested new language' },
              ].map(({ key, label, desc }) => (
                <button
                  key={key}
                  data-testid={`generate-${key}-btn`}
                  onClick={() => handleGenerateResponse(key)}
                  disabled={generatingDoc}
                  className={`text-left p-3 rounded-lg border transition text-sm ${
                    activeResponseType === key
                      ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-200'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <p className="font-medium text-gray-900">{label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                </button>
              ))}
            </div>

            {/* Generated Document Output */}
            {generatingDoc && (
              <div className="flex items-center gap-2 p-4 bg-gray-50 rounded-lg">
                <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                <span className="text-sm text-gray-600">Generating document...</span>
              </div>
            )}

            {generatedDoc && !generatingDoc && (
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 space-y-3" data-testid="generated-document">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-gray-700 capitalize">
                    {activeResponseType?.replace(/_/g, ' ')}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(generatedDoc)}
                  >
                    <Copy className="w-4 h-4 mr-1" /> Copy
                  </Button>
                </div>
                <div className="prose prose-sm max-w-none text-gray-800 whitespace-pre-wrap">
                  {generatedDoc}
                </div>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default ContractAnalysis;
